import { Check, ChevronDown } from 'lucide-react'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import IconChip from './IconChip'
import ModuleThumb from './ModuleThumb'
import { iconFor } from './moduleIcons'
import StatusBadge from './StatusBadge'
import type { TechnologyModuleDetail } from '@/types/technology'

/* One capability, in full, on the sub-page that owns it.
 *
 * Layered so the page can be scanned (owner, 2026-10-18): the name and the
 * one-line summary are always visible, and a status badge ONLY when the
 * module is not live — live is the default, so it carries no mark. The card
 * is an h3 under its group's h2; the paragraph and the bullets sit
 * inside a native <details> under "How it works". Native on purpose — the
 * folded text is still in the server-rendered HTML, so crawlers and
 * find-in-page reach it, and <summary> is keyboard-focusable with no script.
 *
 * Opened, it is two columns on desktop: the paragraph is the claim and the
 * bullets are the evidence for it, side by side so a reader who already
 * believes the claim can skip the evidence.
 *
 * `id` is the module key so every capability is directly linkable. Sales
 * conversations that start from this section will want to send one anchor,
 * not "scroll about halfway down".
 *
 * scroll-mt-52 (44 from sm up, where the section bar is one line) clears the
 * fixed nav and the sticky section bar when an anchor is followed — without it
 * the heading lands underneath the navbar.
 */
export default function ModuleDetailCard({
  module,
  index,
}: {
  module: TechnologyModuleDetail
  index: number
}) {
  const Icon = iconFor(module.key)

  return (
    <ScrollReveal delay={Math.min(index, 4) * 0.05}>
      <article
        id={module.key}
        className="scroll-mt-52 sm:scroll-mt-44 rounded-[1.75rem] border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-6 transition-all duration-200 hover:border-white/[0.16] sm:p-9"
      >
        <div className="flex items-start gap-4">
          <IconChip icon={Icon} size="md" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <h3
                className="text-xl font-semibold text-white sm:text-2xl"
                style={{ fontFamily: 'var(--wavbee-font-display)', letterSpacing: '-0.02em' }}
              >
                {module.name}
              </h3>
              {module.status !== 'live' && (
                <StatusBadge status={module.status} label={module.status_label} />
              )}
            </div>
            <p className="mt-3 text-[0.95rem] leading-[1.75] text-white/75">{module.summary}</p>
          </div>
        </div>

        {/* A screen worth seeing gets a schematic and a way through to it,
            so the card shows something rather than only describing one. Kept
            outside the fold: it is a link, and a link nobody can see is not
            one. */}
        {module.detail_page && (
          <ModuleThumb
            href={module.detail_page.href}
            label={`See ${module.name.toLowerCase()}`}
            shape={module.detail_page.shape}
          />
        )}

        <details className="group mt-5">
          <summary className="marketing-chip inline-flex cursor-pointer list-none items-center gap-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--wavbee-orange)] [&::-webkit-details-marker]:hidden">
            {module.status === 'live' ? 'How it works' : 'What it will do'}
            <span className="sr-only">: {module.name}</span>
            <ChevronDown
              className="h-4 w-4 transition-transform duration-200 group-open:rotate-180"
              aria-hidden="true"
            />
          </summary>

          <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-12">
            <p className="text-[0.95rem] leading-[1.85] text-white/70">{module.detail}</p>

            <ul className="flex flex-col gap-3 lg:border-l lg:border-white/[0.07] lg:pl-12">
              {module.points.map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <Check
                    className="mt-[0.2rem] h-4 w-4 shrink-0 text-[#ff6a1a]"
                    aria-hidden="true"
                  />
                  <span className="text-[0.875rem] leading-[1.7] text-white/60">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        </details>
      </article>
    </ScrollReveal>
  )
}
